import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Check, ChevronDown, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public Client/Company picker used in Shooting-Zusage and Einverständnis forms.
 *
 * Behaviour:
 *  - Loads list from /api/public-clients (no auth required) on mount.
 *  - Shows a searchable dropdown of customer names.
 *  - A checkbox "Nicht in der Liste" reveals a free-text fallback so the user
 *    can still submit when the customer isn't in the system yet (e.g. spontaneous shootings).
 *  - Returns the chosen name as a plain string via onChange(name).
 *
 * Designed to be visually consistent with the dark/glass theme used in the public forms.
 */
const ClientPicker = ({
    value,
    onChange,
    label = 'Kunde wählen',
    placeholder = 'Kunde suchen...',
    fallbackLabel = 'Kunde nicht in der Liste',
    fallbackPlaceholder = 'Kundennamen manuell eingeben...',
    customLabel = 'Anderer Kunde',
    inputClassName = '',
}) => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [useCustom, setUseCustom] = useState(false);
    const wrapperRef = useRef(null);

    // Load public client list once
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_URL}/api/public-clients`)
            .then(res => { if (!cancelled) setClients(res.data || []); })
            .catch(() => { /* keep empty list */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    // If the current value isn't found in the list once loaded, switch to custom mode
    useEffect(() => {
        if (loading || !value) return;
        const found = clients.some(c => c.name === value);
        if (!found && !useCustom) setUseCustom(true);
    }, [loading, clients, value, useCustom]);

    // Close dropdown on outside click
    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const filtered = useMemo(() => {
        if (!query.trim()) return clients;
        const q = query.trim().toLowerCase();
        return clients.filter(c => c.name.toLowerCase().includes(q));
    }, [clients, query]);

    const handleSelect = (name) => {
        onChange(name);
        setOpen(false);
        setQuery('');
    };

    const toggleCustom = (checked) => {
        setUseCustom(checked);
        // When enabling custom mode we keep the current value (user might fine-tune the typed name).
        // When switching back to picker mode we clear so the user explicitly chooses an entry.
        if (!checked) onChange('');
    };

    return (
        <div className="space-y-2" data-testid="client-picker">
            {/* Custom mode toggle */}
            <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none" data-testid="client-picker-toggle">
                <input
                    type="checkbox"
                    checked={useCustom}
                    onChange={(e) => toggleCustom(e.target.checked)}
                    className="w-4 h-4 rounded accent-cyan-500"
                />
                {fallbackLabel}
            </label>

            {useCustom ? (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={fallbackPlaceholder}
                    className={`w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 ${inputClassName}`}
                    data-testid="client-picker-custom-input"
                />
            ) : (
                <div ref={wrapperRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setOpen(o => !o)}
                        className={`w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-left text-white flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-cyan-500/40 ${inputClassName}`}
                        data-testid="client-picker-trigger"
                    >
                        <span className={value ? 'text-white' : 'text-white/40'}>
                            {value || (loading ? 'Lade Kunden…' : label)}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>

                    {open && (
                        <div className="absolute z-[120] mt-1 w-full bg-zinc-900/95 backdrop-blur-xl border border-white/15 rounded-xl shadow-2xl overflow-hidden">
                            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                                <Search className="w-4 h-4 text-white/40" />
                                <input
                                    type="text"
                                    autoFocus
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={placeholder}
                                    className="flex-1 bg-transparent text-white placeholder:text-white/40 focus:outline-none text-sm"
                                    data-testid="client-picker-search"
                                />
                            </div>
                            <div className="max-h-64 overflow-y-auto py-1">
                                {filtered.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-white/50">
                                        Keine Treffer. Aktiviere „{fallbackLabel}" oben.
                                    </div>
                                ) : (
                                    filtered.map(c => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => handleSelect(c.name)}
                                            className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/10 flex items-center gap-2"
                                            data-testid={`client-picker-option-${c.id}`}
                                        >
                                            {c.logo_url && (
                                                <img src={c.logo_url} alt="" className="w-5 h-5 rounded object-contain bg-white/80" />
                                            )}
                                            <span className="flex-1 truncate">{c.name}</span>
                                            {value === c.name && <Check className="w-4 h-4 text-cyan-400" />}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {useCustom && (
                <p className="text-xs text-white/50">
                    {customLabel} — wird im System nur als Text gespeichert.
                </p>
            )}
        </div>
    );
};

export default ClientPicker;
